/**
 * Strategy Builder — plain-language model + DSS spec generator.
 *
 * The builder lets a non-expert compose a strategy through simple form choices.
 * This module defines that friendly model (`StrategyModel`) and translates it
 * into a valid DSS v1.0 document (`buildDssSpec`) that the backtest engine can
 * run. The user never has to know the DSS schema.
 *
 * Design: an entry/exit is a list of "conditions" joined by AND/ANY. Each
 * condition is one of a small set of human-readable templates (price vs a
 * moving average, indicator crossover, RSI thresholds, breakouts, etc.). Every
 * template knows which indicators it needs, so we collect the indicator set
 * automatically and size warmup to cover them.
 */

// ─── Friendly enums ──────────────────────────────────────────────────────────

export type Interval = '1min' | '5min' | '15min' | '30min' | '60min' | 'daily' | 'weekly' | 'monthly';

export type Direction = 'long' | 'short' | 'both';

export type SizingMode = 'equity_fraction' | 'fixed_notional' | 'fixed_qty';

/**
 * A single condition template. Each carries the parameters it needs. The
 * `kind` drives both the UI form and the DSS translation.
 */
export type Condition =
  | { kind: 'price_above_ma'; maType: 'SMA' | 'EMA' | 'WMA'; period: number }
  | { kind: 'price_below_ma'; maType: 'SMA' | 'EMA' | 'WMA'; period: number }
  | { kind: 'ma_cross_up'; fast: number; slow: number; maType: 'SMA' | 'EMA' | 'WMA' }
  | { kind: 'ma_cross_down'; fast: number; slow: number; maType: 'SMA' | 'EMA' | 'WMA' }
  | { kind: 'rsi_below'; period: number; level: number }
  | { kind: 'rsi_above'; period: number; level: number }
  | { kind: 'breakout_high'; period: number }
  | { kind: 'breakdown_low'; period: number }
  | { kind: 'price_change_above'; period: number; pct: number }
  | { kind: 'price_change_below'; period: number; pct: number };

export interface RuleGroup {
  /** How the conditions combine: all must hold, or any one. */
  join: 'all' | 'any';
  conditions: Condition[];
}

export interface RiskSettings {
  sizingMode: SizingMode;
  /** For equity_fraction: 0..1; fixed_notional: dollars; fixed_qty: shares. */
  sizingValue: number;
  /** Optional stop-loss as a percent below/above entry (0 = none). */
  stopLossPct: number;
  /** Optional take-profit as a percent (0 = none). */
  takeProfitPct: number;
  /** Optional time stop in bars (0 = none). */
  timeStopBars: number;
}

export interface RigorSettings {
  walkForward: boolean;
  walkForwardIsMonths: number;
  walkForwardOosMonths: number;
  monteCarlo: boolean;
  monteCarloRuns: number;
  crisisPresets: string[];
}

export interface StrategyModel {
  name: string;
  symbols: string[];
  interval: Interval;
  startDate: string;
  endDate: string;
  initialCash: number;
  direction: Direction;
  entry: RuleGroup;
  exit: RuleGroup;
  risk: RiskSettings;
  costsSlippagePct: number;
  costsFeesPct: number;
  rigor: RigorSettings;
  seed: number;
}

// ─── Defaults + presets ────────────────────────────────────────────────────────

export function defaultModel(): StrategyModel {
  return {
    name: 'My Strategy',
    symbols: [],
    interval: 'daily',
    startDate: '',
    endDate: '',
    initialCash: 100_000,
    direction: 'long',
    entry: { join: 'all', conditions: [{ kind: 'ma_cross_up', fast: 20, slow: 50, maType: 'SMA' }] },
    exit: { join: 'any', conditions: [{ kind: 'ma_cross_down', fast: 20, slow: 50, maType: 'SMA' }] },
    risk: { sizingMode: 'equity_fraction', sizingValue: 0.5, stopLossPct: 0, takeProfitPct: 0, timeStopBars: 0 },
    costsSlippagePct: 0.05,
    costsFeesPct: 0,
    rigor: {
      walkForward: false,
      walkForwardIsMonths: 6,
      walkForwardOosMonths: 3,
      monteCarlo: false,
      monteCarloRuns: 500,
      crisisPresets: [],
    },
    seed: 42,
  };
}

export interface Preset {
  id: string;
  label: string;
  description: string;
  apply: (m: StrategyModel) => StrategyModel;
}

export const PRESETS: Preset[] = [
  {
    id: 'sma_crossover',
    label: 'Trend — SMA Crossover',
    description: 'Go long when the fast SMA crosses above the slow SMA; exit on the reverse cross.',
    apply: (m) => ({
      ...m,
      name: m.name === 'My Strategy' ? 'SMA Crossover' : m.name,
      direction: 'long',
      entry: { join: 'all', conditions: [{ kind: 'ma_cross_up', fast: 20, slow: 50, maType: 'SMA' }] },
      exit: { join: 'any', conditions: [{ kind: 'ma_cross_down', fast: 20, slow: 50, maType: 'SMA' }] },
    }),
  },
  {
    id: 'rsi_reversion',
    label: 'Mean Reversion — RSI',
    description: 'Buy when RSI is oversold (<30), sell when it recovers above 55.',
    apply: (m) => ({
      ...m,
      name: m.name === 'My Strategy' ? 'RSI Mean Reversion' : m.name,
      direction: 'long',
      entry: { join: 'all', conditions: [{ kind: 'rsi_below', period: 14, level: 30 }] },
      exit: { join: 'any', conditions: [{ kind: 'rsi_above', period: 14, level: 55 }] },
    }),
  },
  {
    id: 'donchian_breakout',
    label: 'Breakout — Donchian Channel',
    description: 'Enter when price breaks above the 20-bar high; exit when it breaks below the 10-bar low.',
    apply: (m) => ({
      ...m,
      name: m.name === 'My Strategy' ? 'Donchian Breakout' : m.name,
      direction: 'long',
      entry: { join: 'all', conditions: [{ kind: 'breakout_high', period: 20 }] },
      exit: { join: 'any', conditions: [{ kind: 'breakdown_low', period: 10 }] },
    }),
  },
  {
    id: 'trend_pullback',
    label: 'Trend + Pullback',
    description: 'Long only above the 200 SMA, entering when RSI dips below 40; exit above RSI 60.',
    apply: (m) => ({
      ...m,
      name: m.name === 'My Strategy' ? 'Trend Pullback' : m.name,
      direction: 'long',
      entry: {
        join: 'all',
        conditions: [
          { kind: 'price_above_ma', maType: 'SMA', period: 200 },
          { kind: 'rsi_below', period: 14, level: 40 },
        ],
      },
      exit: { join: 'any', conditions: [{ kind: 'rsi_above', period: 14, level: 60 }] },
    }),
  },
];

// ─── Human-readable condition summary (for the UI) ──────────────────────────────

export function describeCondition(c: Condition): string {
  switch (c.kind) {
    case 'price_above_ma':
      return `Price is above the ${c.period}-bar ${c.maType}`;
    case 'price_below_ma':
      return `Price is below the ${c.period}-bar ${c.maType}`;
    case 'ma_cross_up':
      return `${c.fast}-bar ${c.maType} crosses ABOVE the ${c.slow}-bar ${c.maType}`;
    case 'ma_cross_down':
      return `${c.fast}-bar ${c.maType} crosses BELOW the ${c.slow}-bar ${c.maType}`;
    case 'rsi_below':
      return `RSI(${c.period}) is below ${c.level}`;
    case 'rsi_above':
      return `RSI(${c.period}) is above ${c.level}`;
    case 'breakout_high':
      return `Price breaks above the ${c.period}-bar high`;
    case 'breakdown_low':
      return `Price breaks below the ${c.period}-bar low`;
    case 'price_change_above':
      return `${c.period}-bar price change is above ${c.pct}%`;
    case 'price_change_below':
      return `${c.period}-bar price change is below ${c.pct}%`;
  }
}

/** A blank condition of a given kind, with sensible defaults. */
export function newCondition(kind: Condition['kind']): Condition {
  switch (kind) {
    case 'price_above_ma':
      return { kind, maType: 'SMA', period: 50 };
    case 'price_below_ma':
      return { kind, maType: 'SMA', period: 50 };
    case 'ma_cross_up':
      return { kind, fast: 20, slow: 50, maType: 'SMA' };
    case 'ma_cross_down':
      return { kind, fast: 20, slow: 50, maType: 'SMA' };
    case 'rsi_below':
      return { kind, period: 14, level: 30 };
    case 'rsi_above':
      return { kind, period: 14, level: 70 };
    case 'breakout_high':
      return { kind, period: 20 };
    case 'breakdown_low':
      return { kind, period: 20 };
    case 'price_change_above':
      return { kind, period: 10, pct: 5 };
    case 'price_change_below':
      return { kind, period: 10, pct: -5 };
  }
}

export const CONDITION_KINDS: { kind: Condition['kind']; label: string }[] = [
  { kind: 'ma_cross_up', label: 'Moving-average cross up' },
  { kind: 'ma_cross_down', label: 'Moving-average cross down' },
  { kind: 'price_above_ma', label: 'Price above moving average' },
  { kind: 'price_below_ma', label: 'Price below moving average' },
  { kind: 'rsi_below', label: 'RSI below level (oversold)' },
  { kind: 'rsi_above', label: 'RSI above level (overbought)' },
  { kind: 'breakout_high', label: 'Breakout above N-bar high' },
  { kind: 'breakdown_low', label: 'Breakdown below N-bar low' },
  { kind: 'price_change_above', label: 'Price change % above' },
  { kind: 'price_change_below', label: 'Price change % below' },
];
